// The annual report, /about/annual-report (#438). templates/_annual-report/.
//
// Three small things, and the page works without any of them:
// - Year tabs. The markup is plain links to #2025-26 and #2024-25 with every year shown;
//   this makes them accessible tabs (role=tablist, arrow keys, one panel at a time) and
//   keeps the URL fragment in step, so a year can be linked to directly.
// - Charts grow into place when they scroll into view.
// - Headline numbers count up.
// Animations are skipped entirely for visitors who prefer reduced motion, and without
// IntersectionObserver, leaving the charts and numbers as the markup draws them.
//
// A no-op on every other page, as every module in this bundle must be.
import { onReady } from './dom.js';

var COUNT_MS = 1200;

function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function setUpTabs() {
    var list = document.querySelector('[data-ar-tabs]');
    if (!list) return;

    var tabs = Array.prototype.slice.call(list.querySelectorAll('[data-ar-tab]'));
    var panels = {};
    document.querySelectorAll('[data-ar-panel]').forEach(function (p) {
        panels[p.getAttribute('data-ar-panel')] = p;
    });

    list.setAttribute('role', 'tablist');
    tabs.forEach(function (tab) {
        var id = tab.getAttribute('data-ar-tab');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', id);
        panels[id].setAttribute('role', 'tabpanel');
        panels[id].setAttribute('tabindex', '0');
    });

    function select(id, focus) {
        // A fragment that names a section, not a year (#ar-social), opens the year it's in.
        if (!panels[id]) {
            var target = id && document.getElementById(id);
            var owner = target && target.closest('[data-ar-panel]');
            id = owner ? owner.getAttribute('data-ar-panel') : tabs[0].getAttribute('data-ar-tab');
        }
        tabs.forEach(function (tab) {
            var on = tab.getAttribute('data-ar-tab') === id;
            tab.setAttribute('aria-selected', String(on));
            tab.setAttribute('tabindex', on ? '0' : '-1');
            panels[tab.getAttribute('data-ar-tab')].hidden = !on;
            if (on && focus) tab.focus();
        });
        return id;
    }

    list.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-ar-tab]');
        if (!tab) return;
        e.preventDefault();
        var id = select(tab.getAttribute('data-ar-tab'));
        // replaceState, not the fragment: no jump, and Back leaves the page rather than
        // stepping through every tab the visitor looked at.
        history.replaceState(null, '', '#' + id);
    });

    list.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(document.activeElement);
        if (i === -1) return;
        var next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
        if (next === undefined) return;
        e.preventDefault();
        var tab = tabs[(next + tabs.length) % tabs.length];
        history.replaceState(null, '', '#' + select(tab.getAttribute('data-ar-tab'), true));
    });

    window.addEventListener('hashchange', function () {
        select(window.location.hash.slice(1));
    });

    select(window.location.hash.slice(1));
}

// The final figure, formatted as the markup had it.
function settle(el) {
    var decimals = parseInt(el.getAttribute('data-ar-decimals') || '0', 10);
    el.textContent = parseFloat(el.getAttribute('data-ar-count')).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
        + (el.getAttribute('data-ar-suffix') || '');
}

function countUp(el) {
    var target = parseFloat(el.getAttribute('data-ar-count'));
    var suffix = el.getAttribute('data-ar-suffix') || '';
    var decimals = parseInt(el.getAttribute('data-ar-decimals') || '0', 10);
    var format = function (n) {
        return n.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
    };
    var start = null;
    function frame(t) {
        if (start === null) start = t;
        var p = Math.min((t - start) / COUNT_MS, 1);
        // Ease out, so it slows into the final figure.
        if (p < 1) {
            el.textContent = format(target * (1 - Math.pow(1 - p, 3)));
            window.requestAnimationFrame(frame);
        } else {
            settle(el);
        }
    }
    el.textContent = format(0);
    window.requestAnimationFrame(frame);
}

function setUpAnimation() {
    var charts = document.querySelectorAll('[data-ar-chart]');
    var counters = document.querySelectorAll('[data-ar-count]');
    if ((!charts.length && !counters.length) || reducedMotion() || typeof IntersectionObserver === 'undefined') return;

    var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            if (entry.target.hasAttribute('data-ar-count')) {
                countUp(entry.target);
            } else {
                entry.target.classList.add('is-visible');
            }
            observer.unobserve(entry.target);
        });
    }, { threshold: 0.3 });

    charts.forEach(function (c) {
        c.classList.add('ar-animate');
        observer.observe(c);
    });
    counters.forEach(function (c) { observer.observe(c); });

    // Printing mid-animation would put half-counted numbers and half-grown bars on paper.
    // "Print or save as PDF": the beforeprint handler below settles the charts first.
    document.querySelectorAll('[data-ar-print]').forEach(function (button) {
        button.addEventListener('click', function () { window.print(); });
    });

    window.addEventListener('beforeprint', function () {
        counters.forEach(settle);
        charts.forEach(function (c) { c.classList.add('is-visible'); });
    });
}

onReady(function () {
    setUpTabs();
    setUpAnimation();
});
